// API Service for OlivOak Villa
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
    throw new Error(error.detail || 'Request failed');
  }
  return response.json();
};

// ============ ROOMS API ============
export const roomsApi = {
  // Get all rooms
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/rooms/`);
    return handleResponse(response);
  },

  // Get room by ID
  getById: async (roomId) => {
    const response = await fetch(`${API_URL}/api/rooms/${roomId}`);
    return handleResponse(response);
  },

  // Get room by slug
  getBySlug: async (slug) => {
    const response = await fetch(`${API_URL}/api/rooms/slug/${slug}`);
    return handleResponse(response);
  }
};

// ============ BOOKINGS API ============
export const bookingsApi = {
  // Create a new booking
  create: async (bookingData, token) => {
    const response = await fetch(`${API_URL}/api/bookings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: JSON.stringify(bookingData)
    });
    return handleResponse(response);
  },

  // Get user's bookings
  getMyBookings: async (userId, token) => {
    const response = await fetch(`${API_URL}/api/bookings/user/${userId}`, {
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });
    return handleResponse(response);
  },

  // Get booking by reference
  getByReference: async (reference) => {
    const response = await fetch(`${API_URL}/api/bookings/reference/${reference}`);
    return handleResponse(response);
  },

  // Check room availability
  checkAvailability: async (roomId, checkIn, checkOut) => {
    const response = await fetch(
      `${API_URL}/api/bookings/availability?room_id=${roomId}&check_in=${checkIn}&check_out=${checkOut}`
    );
    return handleResponse(response);
  },

  // Cancel a booking
  cancel: async (bookingId, token) => {
    const response = await fetch(`${API_URL}/api/bookings/${bookingId}/cancel`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });
    return handleResponse(response);
  },

  // Get booked date ranges for a room (for calendar availability)
  getBookedDates: async (roomId) => {
    const response = await fetch(`${API_URL}/api/bookings/room/${roomId}/booked-dates`);
    return handleResponse(response);
  }
};

// ============ REVIEWS API ============
export const reviewsApi = {
  // Get all reviews
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/reviews/`);
    return handleResponse(response);
  },

  // Create a review (uses query parameters as expected by backend)
  create: async (bookingId, rating, reviewText, country, token) => {
    const params = new URLSearchParams({
      booking_id: bookingId,
      rating: rating.toString(),
      review_text: reviewText
    });
    if (country) {
      params.append('country', country);
    }
    
    const response = await fetch(`${API_URL}/api/reviews/?${params.toString()}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });
    return handleResponse(response);
  },

  // Check if booking already has a review
  getByBooking: async (bookingId, token) => {
    const response = await fetch(`${API_URL}/api/reviews/booking/${bookingId}`, {
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });
    // This might 404 if no review exists, which is fine
    if (response.status === 404) {
      return null;
    }
    return handleResponse(response);
  },

  // Get current user's reviews
  getMyReviews: async (token) => {
    const response = await fetch(`${API_URL}/api/reviews/my-reviews`, {
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });
    return handleResponse(response);
  },

  // Get review stats
  getStats: async () => {
    const response = await fetch(`${API_URL}/api/reviews/stats`);
    return handleResponse(response);
  }
};

// ============ PAYMENTS API ============
export const paymentsApi = {
  // Get available payment methods
  getMethods: async () => {
    const response = await fetch(`${API_URL}/api/payments/methods`);
    return handleResponse(response);
  },

  // Initiate payment (web or ecocash)
  initiate: async (bookingId, method = 'web', phone = null, token) => {
    const params = new URLSearchParams({
      booking_id: bookingId,
      method: method
    });
    if (phone) {
      params.append('phone', phone);
    }
    
    const response = await fetch(`${API_URL}/api/payments/initiate?${params.toString()}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });
    return handleResponse(response);
  },

  // Check payment status by booking ID (requires auth)
  checkStatus: async (bookingId, token) => {
    const response = await fetch(`${API_URL}/api/payments/status/${bookingId}`, {
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });
    return handleResponse(response);
  },

  // Check payment status by booking reference (public - for redirect after payment)
  checkStatusByReference: async (bookingReference) => {
    const response = await fetch(`${API_URL}/api/payments/status/check-by-reference?booking_reference=${encodeURIComponent(bookingReference)}`, {
      method: 'POST'
    });
    return handleResponse(response);
  }
};

// ============ ADMIN API ============
export const adminApi = {
  // Get dashboard stats
  getDashboard: async (token) => {
    const response = await fetch(`${API_URL}/api/admin/dashboard`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Get all rooms (including unavailable)
  getRooms: async (token) => {
    const response = await fetch(`${API_URL}/api/admin/rooms`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Create room
  createRoom: async (roomData, token) => {
    const response = await fetch(`${API_URL}/api/admin/rooms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(roomData)
    });
    return handleResponse(response);
  },

  // Update room
  updateRoom: async (roomId, roomData, token) => {
    const response = await fetch(`${API_URL}/api/admin/rooms/${roomId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(roomData)
    });
    return handleResponse(response);
  },

  // Delete room
  deleteRoom: async (roomId, token) => {
    const response = await fetch(`${API_URL}/api/admin/rooms/${roomId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Get all bookings
  getBookings: async (token, status = null) => {
    let url = `${API_URL}/api/admin/bookings`;
    if (status) url += `?status=${status}`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Update booking status
  updateBookingStatus: async (bookingId, status, paymentStatus, token) => {
    const response = await fetch(`${API_URL}/api/admin/bookings/${bookingId}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ status, payment_status: paymentStatus })
    });
    return handleResponse(response);
  },

  // Get all reviews
  getReviews: async (token, approved = null) => {
    let url = `${API_URL}/api/admin/reviews`;
    if (approved !== null) url += `?approved=${approved}`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Approve review
  approveReview: async (reviewId, token) => {
    const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}/approve`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Reject review
  rejectReview: async (reviewId, token) => {
    const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}/reject`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Delete review
  deleteReview: async (reviewId, token) => {
    const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Get setup check (current user info)
  getSetupCheck: async (token) => {
    const response = await fetch(`${API_URL}/api/admin/setup-check`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Get all admin users
  getAdminUsers: async (token) => {
    const response = await fetch(`${API_URL}/api/admin/users`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Create admin user
  createAdminUser: async (userId, token) => {
    const response = await fetch(`${API_URL}/api/admin/users?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Remove admin user
  removeAdminUser: async (userId, token) => {
    const response = await fetch(`${API_URL}/api/admin/users/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Look up user by email
  lookupUserByEmail: async (email, token) => {
    const response = await fetch(`${API_URL}/api/admin/users/lookup?email=${encodeURIComponent(email)}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Get email configuration
  getEmailConfig: async (token) => {
    const response = await fetch(`${API_URL}/api/admin/email-config`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Update email configuration
  updateEmailConfig: async (config, token) => {
    const response = await fetch(`${API_URL}/api/admin/email-config`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(config)
    });
    return handleResponse(response);
  },

  // Send test email
  testEmailConfig: async (email, token) => {
    const response = await fetch(`${API_URL}/api/admin/email-config/test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ email })
    });
    return handleResponse(response);
  },

  // ---- Webhook Config ----

  getWebhookConfigs: async (token) => {
    const response = await fetch(`${API_URL}/api/webhooks/config`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  createWebhookConfig: async (params, token) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/api/webhooks/config?${query}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  updateWebhookConfig: async (configId, params, token) => {
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== null))
    ).toString();
    const response = await fetch(`${API_URL}/api/webhooks/config/${configId}?${query}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  deleteWebhookConfig: async (configId, token) => {
    const response = await fetch(`${API_URL}/api/webhooks/config/${configId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  regenerateWebhookSecret: async (configId, token) => {
    const response = await fetch(`${API_URL}/api/webhooks/config/${configId}/regenerate-secret`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  testWebhook: async (configId, token) => {
    const response = await fetch(`${API_URL}/api/webhooks/config/${configId}/test`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  }
};

// ============ UPLOAD API ============
export const uploadApi = {
  // Upload single image
  uploadImage: async (file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_URL}/api/upload/room-image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    return handleResponse(response);
  },

  // Upload multiple images
  uploadImages: async (files, token) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    
    const response = await fetch(`${API_URL}/api/upload/room-images`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    return handleResponse(response);
  },

  // Delete image
  deleteImage: async (path, token) => {
    const response = await fetch(`${API_URL}/api/upload/room-image?path=${encodeURIComponent(path)}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  }
};

// ============ ROOM TYPES API ============
export const roomTypesApi = {
  // Get all room types
  getAll: async (activeOnly = true) => {
    const response = await fetch(`${API_URL}/api/room-types/?active_only=${activeOnly}`);
    return handleResponse(response);
  },

  // Get room type by ID
  getById: async (id) => {
    const response = await fetch(`${API_URL}/api/room-types/${id}`);
    return handleResponse(response);
  },

  // Create room type (admin)
  create: async (data, token) => {
    const response = await fetch(`${API_URL}/api/room-types/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Update room type (admin)
  update: async (id, data, token) => {
    const response = await fetch(`${API_URL}/api/room-types/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Delete room type (admin)
  delete: async (id, token) => {
    const response = await fetch(`${API_URL}/api/room-types/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  }
};

// ============ CHILD PRICING API ============
export const childPricingApi = {
  // Get all child pricing rules
  getAll: async (roomTypeId = null) => {
    let url = `${API_URL}/api/child-pricing-rules/`;
    if (roomTypeId) url += `?room_type_id=${roomTypeId}`;
    const response = await fetch(url);
    return handleResponse(response);
  },

  // Get rules for a room type
  getByRoomType: async (roomTypeId) => {
    const response = await fetch(`${API_URL}/api/child-pricing-rules/room-type/${roomTypeId}`);
    return handleResponse(response);
  },

  // Create rule (admin)
  create: async (data, token) => {
    const response = await fetch(`${API_URL}/api/child-pricing-rules/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Update rule (admin)
  update: async (id, data, token) => {
    const response = await fetch(`${API_URL}/api/child-pricing-rules/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Delete rule (admin)
  delete: async (id, token) => {
    const response = await fetch(`${API_URL}/api/child-pricing-rules/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  }
};

// ============ TAXES API ============
export const taxesApi = {
  // Get all taxes
  getAll: async (activeOnly = false) => {
    const response = await fetch(`${API_URL}/api/taxes/?active_only=${activeOnly}`);
    return handleResponse(response);
  },

  // Get tax by ID
  getById: async (id) => {
    const response = await fetch(`${API_URL}/api/taxes/${id}`);
    return handleResponse(response);
  },

  // Create tax (admin)
  create: async (data, token) => {
    const response = await fetch(`${API_URL}/api/taxes/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Update tax (admin)
  update: async (id, data, token) => {
    const response = await fetch(`${API_URL}/api/taxes/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Delete tax (admin)
  delete: async (id, token) => {
    const response = await fetch(`${API_URL}/api/taxes/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  }
};

// ============ PROMOTIONS API ============
export const promotionsApi = {
  // Get all promotions
  getAll: async (activeOnly = false) => {
    const response = await fetch(`${API_URL}/api/promotions/?active_only=${activeOnly}`);
    return handleResponse(response);
  },

  // Validate promotion code
  validate: async (code) => {
    const response = await fetch(`${API_URL}/api/promotions/validate/${code}`);
    return handleResponse(response);
  },

  // Get auto-applicable promotions (no code required)
  getAutoApplicable: async () => {
    const response = await fetch(`${API_URL}/api/promotions/auto-applicable`);
    return handleResponse(response);
  },

  // Get promotion by ID
  getById: async (id) => {
    const response = await fetch(`${API_URL}/api/promotions/${id}`);
    return handleResponse(response);
  },

  // Create promotion (admin)
  create: async (data, token) => {
    const response = await fetch(`${API_URL}/api/promotions/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Update promotion (admin)
  update: async (id, data, token) => {
    const response = await fetch(`${API_URL}/api/promotions/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Delete promotion (admin)
  delete: async (id, token) => {
    const response = await fetch(`${API_URL}/api/promotions/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  }
};

// ============ BILLING API ============
export const billingApi = {
  // Calculate billing
  calculate: async (data) => {
    const response = await fetch(`${API_URL}/api/billing/calculate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Get room type pricing info
  getPricingInfo: async (roomTypeId) => {
    const response = await fetch(`${API_URL}/api/billing/room-type/${roomTypeId}/pricing-info`);
    return handleResponse(response);
  }
};

// ============ ADD-ONS API ============
export const addonsApi = {
  // Get all add-ons
  getAll: async (activeOnly = false) => {
    const response = await fetch(`${API_URL}/api/addons/?active_only=${activeOnly}`);
    return handleResponse(response);
  },

  // Get add-on by ID
  getById: async (id) => {
    const response = await fetch(`${API_URL}/api/addons/${id}`);
    return handleResponse(response);
  },

  // Create add-on (admin)
  create: async (data, token) => {
    const response = await fetch(`${API_URL}/api/addons/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Update add-on (admin)
  update: async (id, data, token) => {
    const response = await fetch(`${API_URL}/api/addons/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Delete add-on (admin)
  delete: async (id, token) => {
    const response = await fetch(`${API_URL}/api/addons/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  }
};

// ============ GALLERY API ============
export const galleryApi = {
  // Public: get categories
  getCategories: async () => {
    const response = await fetch(`${API_URL}/api/gallery/categories`);
    return handleResponse(response);
  },

  // Public: get images (live only by default)
  getImages: async (categoryId = null, liveOnly = true) => {
    const params = new URLSearchParams();
    if (categoryId) params.append('category_id', categoryId);
    params.append('live_only', liveOnly);
    const response = await fetch(`${API_URL}/api/gallery/images?${params}`);
    return handleResponse(response);
  },

  // Admin: create category
  createCategory: async (data, token) => {
    const response = await fetch(`${API_URL}/api/gallery/admin/categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Admin: update category
  updateCategory: async (id, data, token) => {
    const response = await fetch(`${API_URL}/api/gallery/admin/categories/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Admin: delete category
  deleteCategory: async (id, token) => {
    const response = await fetch(`${API_URL}/api/gallery/admin/categories/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Admin: get all images (including non-live)
  adminGetImages: async (categoryId, token) => {
    const params = new URLSearchParams();
    if (categoryId) params.append('category_id', categoryId);
    const response = await fetch(`${API_URL}/api/gallery/admin/images?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Admin: create image record
  createImage: async (data, token) => {
    const response = await fetch(`${API_URL}/api/gallery/admin/images`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Admin: update image
  updateImage: async (id, data, token) => {
    const response = await fetch(`${API_URL}/api/gallery/admin/images/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // Admin: toggle live
  toggleLive: async (id, token) => {
    const response = await fetch(`${API_URL}/api/gallery/admin/images/${id}/toggle-live`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Admin: delete image
  deleteImage: async (id, token) => {
    const response = await fetch(`${API_URL}/api/gallery/admin/images/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Upload gallery image file
  uploadImage: async (file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/api/upload/gallery-image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    return handleResponse(response);
  }
};

export default {
  rooms: roomsApi,
  bookings: bookingsApi,
  reviews: reviewsApi,
  payments: paymentsApi,
  admin: adminApi,
  upload: uploadApi,
  roomTypes: roomTypesApi,
  childPricing: childPricingApi,
  taxes: taxesApi,
  promotions: promotionsApi,
  billing: billingApi,
  addons: addonsApi,
  gallery: galleryApi
};
